import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { IconArrowForward } from '@medifind/icons';
import { IconArrowBack } from '@medifind/icons';
import { changeURLParams } from '@medifind/router';
import { useRouter } from '@medifind/router';
import { Button } from '@medifind/shared-basic-components';
import { Skeleton } from '@medifind/shared-basic-components';
import { numberWithCommas } from '@medifind/utils';
import { logEventGA4, useClinicalTrialStepper } from '@medifind/zustand';
import { findNextValidStep, findPreviousStep } from '../../../utils/stepperUtils';
import styles from './StepShared.module.scss';

const Step = ({
  children,
  icon,
  title,
  titleId,
  subTitle,
  optionsRowStyles,
  optionsColStyles,
  trialCount,
  showTrialCount,
  footer,
  isValue,
  showOptional,
  nextButtonLabelOverride,
  nextButtonActionOverride,
  prevButtonLabelOverride,
  prevButtonActionOverride,
  loading,
}) => {
  const router = useRouter();

  const { query } = router;
  const { step: queryStep } = query;

  const steps = useClinicalTrialStepper((state) => state.steps);

  const nextValidStep = findNextValidStep({ steps, currentStep: queryStep || 0 });
  const previousValidStep = findPreviousStep({ steps, currentStep: queryStep || 0 });
  const currentStep = steps?.[queryStep];

  const handleGoBack = () => {
    changeURLParams(router, { step: previousValidStep });
  };

  const handleNext = () => {
    if (nextButtonActionOverride) {
      return nextButtonActionOverride;
    }
    const nextStep = queryStep && isNaN(nextValidStep) ? 0 : nextValidStep;
    const { stepLabel, key } = currentStep || { stepLabel: '', key: '' };
    if (key === 'contact') {
      logEventGA4('trial_search', { step: stepLabel, action: 'skipped' });
    } else {
      logEventGA4('trial_search', { step: stepLabel });
    }
    changeURLParams(router, { step: nextStep });
  };

  const nextDisabled = (step) => {
    if (step) {
      const { data, isRequired } = step;
      return isRequired && !data?.value;
    } else return !isValue;
  };

  const handleGoToResults = () => {
    const { stepLabel } = currentStep;
    logEventGA4('trial_Search', { step: stepLabel, action: 'skipped' });
    changeURLParams(router, { step: steps.length + 1 });
  };

  const nextLabel = (step) => {
    if (nextButtonLabelOverride) {
      return nextButtonLabelOverride;
    }
    if (step) {
      const { data } = currentStep;
      if (showOptional && !data?.value) {
        return 'Skip';
      } else {
        return 'Next';
      }
    } else {
      return 'Next';
    }
  };

  const getResultsButton = ({ trialCount }) => {
    if (trialCount === undefined) {
      return null;
    }
    return loading ? (
      <div className={styles['no-results-container']}>
        <Button
          className={classNames(styles['btn'], styles['results-button'])}
          disabled={loading}
          label={<Skeleton width="158px" height="24px" />}
        />
      </div>
    ) : trialCount > 0 ? (
      <Button
        className={classNames(styles['btn'], styles['results-button'])}
        onClick={() => (trialCount > 0 ? handleGoToResults() : {})}
        label={`Trials Found: ${numberWithCommas(trialCount)} `}
      />
    ) : (
      <div className={styles['no-results-container']}>
        <p>No matching trials were found.</p>
        <p>Please revise your search.</p>
      </div>
    );
  };

  const nextLab = nextLabel(currentStep);
  const isNextDisabled = nextDisabled(currentStep);
  const noResults = steps?.length === queryStep && !trialCount;

  return (
    <div className={styles['step-container']}>
      <Row>
        <Col sm={12} md={2} lg={{ offset: 1, span: 1 }} xl={{ offset: 1, span: 1 }} className={styles['icon-container']}>
          {icon && <div className={classNames(styles['icon'])}>{icon}</div>}
        </Col>
        <Col sm={12} md={10} lg={9} xl={7}>
          <Row>
            <Col {...(titleId ? { id: titleId } : null)} className={styles['step-description']}>
              <div className={styles['title']}>
                {title} {showOptional && <span className={styles['optional-text']}>(Optional)</span>}
              </div>
              <div className={styles['subtitle']}>{subTitle}</div>
            </Col>
          </Row>
          {children && (
            <Row className={classNames(styles['options-row'], optionsRowStyles)}>
              <Col className={classNames(styles['options-col'], optionsColStyles)}>{children}</Col>
            </Row>
          )}
          <Row className={styles['nav-row']}>
            <Col xs={{ order: 2, span: 6 }} lg={{ order: 1, span: 3 }} className={styles['prev-col']}>
              {queryStep > 0 && (
                <Button
                  label={prevButtonLabelOverride || 'Back'}
                  onClick={prevButtonActionOverride || handleGoBack}
                  classes={{ iconBefore: styles['navigation-button-icon'] }}
                  iconBefore={<IconArrowBack />}
                  className={styles['btn']}
                />
              )}
            </Col>
            <Col xs={{ order: 1, span: 12 }} lg={{ order: 2, span: 6 }} className={styles['trial-count-col']}>
              {showTrialCount && !noResults && getResultsButton({ trialCount })}
            </Col>
            <Col xs={{ order: 3, span: 6 }} lg={{ order: 3, span: 3 }} className={styles['next-col']}>
              {!noResults && (
                <Button
                  label={nextLab}
                  onClick={handleNext}
                  icon={<IconArrowForward />}
                  classes={{ icon: styles['navigation-button-icon'] }}
                  disabled={isNextDisabled}
                  className={classNames(styles['btn'], { [styles['btn--disabled']]: isNextDisabled })}
                />
              )}
            </Col>
          </Row>
          <Row className={styles['footer-row']}>
            <Col>{footer && footer}</Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

Step.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.node,
  optionsRowStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  optionsColStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  title: PropTypes.string,
  subTitle: PropTypes.string,
  trialCount: PropTypes.number,
  showTrialCount: PropTypes.bool,
  footer: PropTypes.node,
  isValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
  showOptional: PropTypes.bool,
  loading: PropTypes.bool,
  nextButtonLabelOverride: PropTypes.func,
  nextButtonActionOverride: PropTypes.func,
  prevButtonLabelOverride: PropTypes.func,
  prevButtonActionOverride: PropTypes.func,
};

export default Step;
